import { ConnectorFormDetails, Input, Label } from "@simplicity/ui";
import { TablesEditor, type Schedules, type TableConfig } from "../tables";
import { EntityIdsInput } from "./EntityIdsInput";
import type { ConnectorFormProps } from "./GenericVarsForm";

/** Multi-entity `vars.entityIds` editor. Extracted so SkywardForm stays under the line cap. */
function EntityIdsField(props: Pick<ConnectorFormProps, "vars" | "onVarsChange" | "clearBanner">) {
  const entityIds = Array.isArray(props.vars.entityIds)
    ? props.vars.entityIds.filter((v): v is number => typeof v === "number")
    : [];

  function updateEntityIds(ids: number[]) {
    const next = { ...props.vars };
    if (ids.length === 0) {
      delete next.entityIds;
    } else {
      next.entityIds = ids;
    }
    props.onVarsChange(next);
    props.clearBanner();
  }

  return (
    <div>
      <Label htmlFor="sky-entity-ids" className="form-label">
        Entity IDs (multi-entity)
      </Label>
      <EntityIdsInput id="sky-entity-ids" value={entityIds} onChange={updateEntityIds} />
      <p className="mt-1 text-xs text-muted-foreground">
        Pull every table for each listed entity. Leave empty to use the single Entity ID above.
      </p>
    </div>
  );
}

export function SkywardForm(props: ConnectorFormProps) {
  const tables = Array.isArray(props.vars.tables) ? (props.vars.tables as TableConfig[]) : [];
  const schedules = (
    props.vars.schedules != null &&
    typeof props.vars.schedules === "object" &&
    !Array.isArray(props.vars.schedules)
      ? props.vars.schedules
      : {}
  ) as Schedules;

  const entityId = typeof props.vars.entityId === "number" ? props.vars.entityId : "";
  const bronzeDataset = (props.vars.bronzeDataset as string) ?? "";
  const silverDataset = (props.vars.silverDataset as string) ?? "";

  function updateField(key: string, value: string | number) {
    const next = { ...props.vars };
    if (value === "" || value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    props.onVarsChange(next);
    props.clearBanner();
  }

  return (
    <>
      <div>
        <Label htmlFor="sky-entity-id" className="form-label">
          Entity ID
        </Label>
        <Input
          id="sky-entity-id"
          type="number"
          value={entityId}
          onChange={(e) => {
            const val = e.target.value;
            updateField("entityId", val ? Number(val) : "");
          }}
          placeholder="Required — Skyward entity ID"
          className="font-mono"
        />
      </div>
      <EntityIdsField
        vars={props.vars}
        onVarsChange={props.onVarsChange}
        clearBanner={props.clearBanner}
      />
      <TablesEditor
        tables={tables}
        schedules={schedules}
        onChange={(change) => {
          props.onVarsChange({ ...props.vars, ...change });
          props.clearBanner();
        }}
        connectorType={props.connectorType}
        connectorId={props.connectorId}
      />
      {!props.hideAdvanced && (
        <ConnectorFormDetails
          summary="Advanced Options"
          className="mt-4 rounded border border-border/50"
        >
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="sky-bronze-dataset" className="form-label">
                Bronze Dataset
              </Label>
              <Input
                id="sky-bronze-dataset"
                type="text"
                value={bronzeDataset}
                onChange={(e) => updateField("bronzeDataset", e.target.value)}
                placeholder={`Default: bronze__${props.connectorPrefix}`}
                className="font-mono"
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="sky-silver-dataset" className="form-label">
                Silver Dataset
              </Label>
              <Input
                id="sky-silver-dataset"
                type="text"
                value={silverDataset}
                onChange={(e) => updateField("silverDataset", e.target.value)}
                placeholder={`Default: silver__${props.connectorPrefix}`}
                className="font-mono"
              />
            </div>
          </div>
        </ConnectorFormDetails>
      )}
    </>
  );
}
